import { Injectable } from '@angular/core';
import { DataURLCommonService } from '@vex/services/data-url-common.service';
import { ToastrService } from 'ngx-toastr';
import {
  FormGroup,
  FormArray,
  FormBuilder,
  AbstractControl,
  FormControl,
  Validators,
  FormGroupName
} from '@angular/forms';
import { RxwebValidators } from '@rxweb/reactive-form-validators';
@Injectable({
  providedIn: 'root'
})
export class FormControlVisibilityService {
  constructor(
    private dataUrlCommonService: DataURLCommonService,
    private toastrService: ToastrService
  ) {}

  /** This function sets validator on form control. It will only apply validations if the attribute visibility is true
   * otherwise it will remove all validations from the control.
   * @param {object} attribute - An object of blueprint collection attribute. Used to get a control from form, to check visibility
   * and to check the type of an attribute.
   * @param {FormGroup} form - An object of form group. Retrieve a control from from and apply or remove validations
   */
  updateAttributeValidation(attribute, form, scope, isCallFromTemplate: boolean = false, isCallFromBP: boolean = false, source: string = '', isNeedRequired: boolean = true) {
    var control = form?.get(attribute.Name);
    if (control) {
      // This is working, but just to be on safe side - test thoroughly.
      if (attribute.IsVisible) {
        if (attribute.LastIsVisibleStatus == false) {
          control.enable();
        }
        if (!isCallFromTemplate) {
          control.enable();
        }
        // Validation will not apply on list and complex attribute.
        // Validation will apply on list and complex child attribute like (input, dropdown)
        if ((attribute.AttributeType && !(attribute.AttributeType.IsList || attribute.AttributeType.IsComplex))) {
          control.setValidators([...this.getValidationProps(attribute, isCallFromBP, source, isNeedRequired)]);
          if (
            attribute.DefaultValue &&
            attribute.AttributeType.BaseType != 'bool' &&
            !control.value
          ) {
            control.setValue(attribute.DefaultValue);
            this.toastrService.info(
              attribute.DefaultValue + ' is a default value of ' + attribute.DisplayName
            );
          }
          if (attribute.AttributeType.BaseType == 'bool' && control.value == null) {
            control.setValue(this.getBoolValue(attribute));
          }
        } else if (attribute.AttributeType && attribute.AttributeType.IsList && !attribute.IsDropdown) {
          if (!((control.controls && control.controls.length > 0) || (control.value && control.value.length > 0))) {
            if (attribute.LastIsVisibleStatus == false) {
              scope.addRow(attribute, form, form.value);
            }
          }
        } else if (attribute.AttributeType && attribute.AttributeType.IsComplex) {
          let complexForms = control as FormArray;
          if (attribute.LastIsVisibleStatus == false) {
            scope.addRow(attribute, form, form.value);
            if (isCallFromTemplate) {
              scope.isRunAgainSetFormControlVisibility = true;
            }
          } else {
            if (isCallFromTemplate) {
              complexForms.controls.forEach((complexForm: FormGroup) => {
                let _complexAttributes = complexForm.get('_ComplexAttributes').value;
                _complexAttributes = _complexAttributes
                  .filter(
                    (_complexAttribute) =>
                      (_complexAttribute.DataGeneratedBy == 'CSP' &&
                        _complexAttribute.IsParentProperty == true) ||
                      _complexAttribute.DataGeneratedBy == 'User'
                  )
                  .forEach((_complexAttribute) => {
                    this.updateAttributeValidation(
                      _complexAttribute,
                      complexForm,
                      scope,
                      isCallFromTemplate, isCallFromBP, source, isNeedRequired
                    );
                  });
              });
            }
          }
        }
        if (attribute.LastIsVisibleStatus == false) {
          control.updateValueAndValidity({ onlySelf: false, emitEvent: false });
        }
        if (!isCallFromTemplate) {
          control.updateValueAndValidity({ onlySelf: false, emitEvent: false });
        }
        attribute.LastIsVisibleStatus = true;
      } else {
        if (attribute.LastIsVisibleStatus == true) {
          control.disable();
        }
        if (!isCallFromTemplate) {
          control.disable();
        }
        if (attribute.AttributeType && (attribute.AttributeType.IsComplex || attribute.AttributeType.IsList)) {
          control.controls = [];
          control.value = [];
        } else {
          control.setValue(null);
          attribute.Value = null;
        }
        control.clearValidators();
        control.setValidators(null);
        control.updateValueAndValidity({ onlySelf: false });
        if (attribute.LastIsVisibleStatus == true) {
          attribute.LastIsVisibleStatus = false;
          if (isCallFromTemplate) {
            scope.setFormControlVisibility(control.value, attribute.Name, false, true);
          }
        }
        attribute.LastIsVisibleStatus = false;
      }
    }
  }

  getBoolValue(attribute, previousValue: any = null, status: boolean = false) {
    if (status && previousValue !== undefined && previousValue !== null && previousValue !== '') {
      if (previousValue == 'False') {
        previousValue = 'false';
      }
      if(previousValue !== 'false' && previousValue !== 'true') {
      if(previousValue === true){
        previousValue = 'true'
      }else{
        previousValue = 'false';
      }
      }
      return Boolean(JSON.parse(previousValue));
    }
    if (attribute.Value === 'true' || attribute.Value === 'false') {
      return Boolean(JSON.parse(attribute.Value));
    }
    if (attribute.DefaultValue === 'true' || attribute.DefaultValue === 'false') {
      return Boolean(JSON.parse(attribute.DefaultValue));
    }
    if (attribute.Value === true) {
      return true;
    }
    if (attribute.IsMandatory) {
      return false;
    } else {
      return null;
    }
  }

  /** Returns left property name from the expression.
   * @param {string} condition - An expression from which you want to extract property name.
   */
  getPropertyNameFromCondition(condition: string): string {
    let result = '';
    if (condition.indexOf('==') > 0) {
      result = condition
        .replace('{', '')
        .replace('}', '')
        .replace('(', '')
        .replace(')', '')
        .split('==')[0]
        .trim();
    } else if (condition.indexOf('!=') > 0) {
      result = condition
        .replace('{', '')
        .replace('}', '')
        .replace('(', '')
        .replace(')', '')
        .split('!=')[0]
        .trim();
    }
    return result;
  }

  /** Finds and return attribute value
   * Priorites to find
   * 1. mainForm.
   * 2. collection.
   * 3. collections.
   * @param {FormGroup} mainForm- An object of blueprint collection or resource form.
   * @param {object} collections- An array of blueprint collections or resources, system use this collection if given attribute keys not found in collection.
   * @param {object} collection- An object of blueprint collection or resource.
   * @param {Array} attributeKeys- List of attribute keys.
   */
  matchWithParenAttributeValue(mainForm, collections, collection, attributeKeys) {
    let isFindInAllCollection: boolean = true;
    if (mainForm !== undefined) {
      let last_resource: any = Object.assign({}, mainForm.value);
      for (var i = 1; i < attributeKeys.length; i++) {
        if (last_resource[attributeKeys[i]] != undefined && last_resource[attributeKeys[i]] != '') {
          last_resource = last_resource[attributeKeys[i]];
          if (i == attributeKeys.length - 1) {
            return last_resource;
          } else {
            last_resource = last_resource[0];
          }
        }
      }
    }
    if (collection) {
      let data = collection.CollectionAttributeValues?.find(
        (m) => m.Name == 'Terraform Resource Name' && m.Value == attributeKeys[0]
      );
      if (data) {
        isFindInAllCollection = false;
        return this.getLabelValue(collection.BlueprintCollectionAttributes || collection.CollectionEntryAttributes, attributeKeys);
      }
    }
    if (isFindInAllCollection) {
      let parents = collections.filter((m) => !m.IsContainer);
      for (var i = 0; i < parents.length; i++) {
        let collection = parents[i];
        let data = collection.CollectionAttributeValues?.find(
          (m) => m.Name == 'Terraform Resource Name' && m.Value == attributeKeys[0]
        );
        if (data || collection.ResourceName == attributeKeys[0]) {
          return this.getLabelValue(collection.BlueprintCollectionAttributes || collection.CollectionEntryAttributes, attributeKeys);
        }
      }
    }
  }

  /** Loop through each attribute key and returns last attribute value from a given resource.
   * @param {object} collection- An object of blueprint resource.
   * @param {Array} attributeKeys- List of attribute keys. Get second index value and find it in the collection entry attributes of a collection
   */
  getLabelValue(attributes, attributeKeys) {
    let Value = '';
    let label = attributes.find((m) => m.Name == attributeKeys[1]);
    if (attributeKeys.length > 2) {
      if (label && label.Value) {
        let last_resource: any = this.dataUrlCommonService.getValidJsonValue(
          label.Value,
          label.Name
        );
        if (last_resource) {
          for (var i = 1; i < attributeKeys.length; i++) {
            if (
              last_resource[attributeKeys[i]] != undefined &&
              last_resource[attributeKeys[i]] != ''
            ) {
              last_resource = last_resource[attributeKeys[i]];
              if (i == attributeKeys.length - 1) {
                Value = last_resource;
                break;
              } else last_resource = last_resource[0];
            }
          }
        }
      }
    } else {
      if (label) {
        if (label.AttributeType && label.AttributeType.IsList) {
          let value = this.dataUrlCommonService.getValidJsonValue(label.Value, label.Name);
          value = value[attributeKeys[1]];
          Value = value.join(', ');
        } else {
          Value = label.Value;
        }
      }
    }
    return Value;
  }

  getValidationProps(attribute: any, isCallFromBP: boolean = false, source: string = '', isNeedRequired: boolean = true) {
    let controlProps = [];
    if (attribute.Hide) {
      return controlProps;
    }
    controlProps = this.lengthAndRegexValidation(attribute);

    // Need to discuss for below condition
    // if (
    //     attribute.DataUrl &&
    //     attribute.DataUrl.indexOf('diagram') !== -1 &&
    //     this.source == 'Template'
    // ) {
    //     controlProps.push(Validators.required);
    // }

    // 1. Mandatory field will apply on template selector page.
    // 2. Otherwise user can save data without fill mandatory field in right side config panel
    // 3. Mandatory field will apply on add resource from Day2Manager.
    // 4. Mandatory field will apply on add resource from Inventory.
    if (attribute.IsMandatory) {
      if (isCallFromBP) {
        if (source == 'Template' || source == 'Day2Manager' || source == 'Inventory') {
          controlProps.push(Validators.required);
        }
      } else {
        if(isNeedRequired) {
          controlProps.push(Validators.required);
        }
      }
    }
    return controlProps;
  }

  getValidationPropsForComplex(
    parentAttribute: any,
    attribute: any,
    isCallFromBP: boolean = false,
    source: string = '',
    isNeedRequired: boolean = true
  ) {
    let controlProps = this.lengthAndRegexValidation(attribute);
    if (parentAttribute.IsMandatory && attribute.IsMandatory) {
      if (isCallFromBP) {
        if (source == 'Template' || source == 'Day2Manager' || source == 'Inventory') {
          controlProps.push(Validators.required);
        }
      } else {
        if(isNeedRequired) {
          controlProps.push(Validators.required);
        }
      }
    }
    if (parentAttribute.AttributeType && parentAttribute.AttributeType.Name == 'tags' && attribute.Name == 'Key') {
      controlProps.push(RxwebValidators.unique());
    }
    return controlProps;
  }

  /** Returns list of validations for a given attribute and we will not add any validations for hidden attributes, possible validations
   * * minLength: Applies only string type and tell us how many min characters atleast we should have.
   * * maxLength: Applies only string type and tell us how many max characters atleast we should have.
   * * max: Applies only int and decimal type and tell us max value.
   * * min: Applies only int and decimal type and tell us min value.
   * * cidr: Applies when attribute type name is cidr then we add custom validator cte to validate this with its parent cidr.
   * * pattern: Applies if attribute type is string and regex enabled for an attribute then we enabled regex validation by providing expression.
   * @param {object} attribute - An object of blueprint collection attribute.
   */
  lengthAndRegexValidation(attribute) {
    // Min and Max value use When attribute define in modify collection page
    // MaxLength and MinLength use When attribute define in attribute type page
    let controlProps = [];
    let baseType = attribute.AttributeType;
    if(baseType) {
      if (
        ((((attribute.MinLength && attribute.MinLength > 0) ||
          (!baseType.IsList && attribute.Min && attribute.Min > 0)) &&
          attribute.BaseType == 'string') ||
          (!attribute.IsList &&
            baseType.MinLength &&
            baseType.MinLength > 0 &&
            baseType.BaseType == 'string')) &&
        !attribute.IsComplex
      ) {
        if (
          (attribute.MinLength && attribute.MinLength > 0) ||
          (attribute.Min && attribute.Min > 0)
        ) {
          controlProps.push(Validators.minLength(attribute.Min || attribute.MinLength));
        } else {
          controlProps.push(Validators.minLength(baseType.MinLength));
        }
      }
      if (
        ((((attribute.MinLength && attribute.MinLength > 0) ||
          (!baseType.IsList && attribute.Min && attribute.Min > 0)) &&
          (attribute.BaseType == 'int' || attribute.BaseType == 'decimal')) ||
          (!attribute.IsList &&
            baseType.MinLength &&
            baseType.MinLength > 0 &&
            (baseType.BaseType == 'int' || baseType.BaseType == 'decimal'))) &&
        !attribute.IsComplex
      ) {
        if (
          (attribute.MinLength && attribute.MinLength > 0) ||
          (attribute.Min && attribute.Min > 0)
        ) {
          controlProps.push(Validators.min(attribute.Min || attribute.MinLength));
        } else {
          controlProps.push(Validators.min(baseType.MinLength));
        }
      }
  
      if (
        ((((attribute.MaxLength && attribute.MaxLength > 0) ||
          (!baseType.IsList && attribute.Max && attribute.Max > 0)) &&
          attribute.BaseType == 'string') ||
          (!attribute.IsList &&
            baseType.MaxLength &&
            baseType.MaxLength > 0 &&
            baseType.BaseType == 'string')) &&
        !attribute.IsComplex
      ) {
        if (
          (attribute.MaxLength && attribute.MaxLength > 0) ||
          (attribute.Max && attribute.Max > 0)
        ) {
          controlProps.push(Validators.maxLength(attribute.Max || attribute.MaxLength));
        } else {
          controlProps.push(Validators.maxLength(baseType.MaxLength));
        }
      }
      if (
        ((((attribute.MaxLength && attribute.MaxLength > 0) ||
          (!baseType.IsList && attribute.Max && attribute.Max > 0)) &&
          (attribute.BaseType == 'int' || attribute.BaseType == 'decimal')) ||
          (!attribute.IsList &&
            baseType.MaxLength &&
            baseType.MaxLength > 0 &&
            (baseType.BaseType == 'int' || baseType.BaseType == 'decimal'))) &&
        !attribute.IsComplex
      ) {
        if (
          (attribute.MaxLength && attribute.MaxLength > 0) ||
          (attribute.Max && attribute.Max > 0)
        ) {
          controlProps.push(Validators.max(attribute.Max || attribute.MaxLength));
        } else {
          controlProps.push(Validators.max(baseType.MaxLength));
        }
      }
  
      if (
        (attribute.Regex || baseType.Regex) &&
        (baseType.BaseType == 'string' || baseType.BaseType == 'int') &&
        !attribute.IsComplex
      ) {
        controlProps.push(Validators.pattern(attribute.Regex || baseType.Regex));
      }
    }

    return controlProps;
  }
}
